import { Box, Grid } from 'components/box';
import { Heading, Text } from 'components/typography';
import { memo, useId, useMemo } from 'react';
import ClockIcon from 'icons/clock.svg';
import { css } from '@emotion/react';
import Color from '../../util/color';
import { ClientOnly } from 'components/_shared/widgets/render-control';
import { zIndices } from 'constants/z-indices';
import { SoldOutHeaderInterface, SoldOutHeaderMessage } from 'types/types';

/**
 * NOTE: This function was adapted from a Stackoverflow answer. It does not implement the most
 * efficient hash generation, nor does it guarantee no collisions since it only returns a 32 bit
 * hash. But for this use case, it's more than sufficient and has the benefit
 * of me understanding what it does, which can't be said for the other hashing functions on this forum.
 * @see https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
 */
const generateHash = (s: string) => {
  let hash = 0;
  for (const char of s) {
    hash = (hash << 5) - hash + char.charCodeAt(0);
    hash |= 0; // Constrain to 32bit integer
  }
  return hash;
};

const generateRandomIndexFromId = (id: string, max: number) =>
  Math.abs(generateHash(id) % max);

const soldOutHeaderFallback: SoldOutHeaderMessage = {
  title: 'Sold Out',
  header: 'This deal is sold out!',
};

const timedOutHeader: SoldOutHeaderMessage = {
  title: 'Expired',
  header: 'This deal has expired!',
};

const getSoldOutHeaderForDealType = ({
  dealTypes,
  soldOutHeaders,
  maskId,
}: {
  dealTypes?: string[];
  soldOutHeaders: SoldOutHeaderInterface[];
  maskId: string;
}) => {
  const soldOutMessages = dealTypes
    ? soldOutHeaders.reduce((acc: SoldOutHeaderMessage[], header) => {
        if (dealTypes.includes(header.type)) {
          acc.push(...header.messages);
        }
        return acc;
      }, [])
    : soldOutHeaders.find(h => h.type === 'Default')?.messages;

  if (!soldOutMessages || !Array.isArray(soldOutMessages)) {
    return soldOutHeaderFallback;
  }

  const randomIndex = generateRandomIndexFromId(maskId, soldOutMessages.length);
  return soldOutMessages[randomIndex];
};

export const SoldOutMask = memo(
  ({
    clock,
    isDark,
    headingFontSize,
    text,
    isOnSearch,
    soldOutHeaders,
    dealTypes,
    isTimedOut,
  }: {
    isTimedOut?: boolean;
    clock?: boolean;
    isDark?: boolean;
    headingFontSize?: (string | number | null)[];
    text?: boolean;
    isOnSearch?: boolean;
    soldOutHeaders?: SoldOutHeaderInterface[];
    dealTypes?: string[];
  }) => {
    /**
     * NOTE: According to the docs, useId should not be used at all for SSR if the component
     * tree on client will differ from server. In our case, however, the component tree
     * for this sold out mask should not change between renders, therefore we should be safe.
     * @see https://react.dev/reference/react/useId
     */
    const maskId = useId();
    const header = useMemo(() => {
      if (isTimedOut) return timedOutHeader;
      if (!soldOutHeaders?.length) {
        return soldOutHeaderFallback;
      }

      return getSoldOutHeaderForDealType({
        dealTypes,
        soldOutHeaders,
        maskId,
      });
    }, [isTimedOut, soldOutHeaders, dealTypes, maskId]);

    return (
      <Grid
        gridRowGap="0"
        css={theme => css`
          ${isOnSearch &&
          css`
            border-radius: ${theme.radii[3]}px;
          `};
          background-color: ${Color(isDark ? '#E3E4E8' : 'white')
            .alpha(isDark ? 0.7 : 0.9)
            .string()};
          font-family: ${theme.fonts.header};
          font-weight: ${theme.fontWeights.bolder};
          text-align: center;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: ${zIndices.SOLD_OUT_MASK};
          color: ${theme.colors.darkGrey};
          place-items: center;
        `}
      >
        <Box>
          {clock && <ClockIcon alt="clock" />}
          {text && (
            <ClientOnly>
              <>
                <Heading
                  aria-label={header?.title}
                  fontSize={headingFontSize || [6, null, 7]}
                  fontWeight="bolder"
                  color="darkGrey"
                  css={css`
                    text-transform: uppercase;
                  `}
                >
                  {header?.title}
                </Heading>
                <Text
                  fontSize={[1, 2, isOnSearch ? 2 : 3]}
                  css={theme => css`
                    margin: 5px 0;
                    @media ${theme.mediaQueries.tabletDown} {
                      line-height: 1.2em;
                      margin-bottom: 1rem;
                    }
                  `}
                >
                  {header?.header}
                </Text>
                {header?.body && (
                  <Text
                    aria-label="This deal is sold out!"
                    fontSize={[1, 1, 2]}
                    css={theme => css`
                      @media ${theme.mediaQueries.tabletDown} {
                        line-height: 1.2em;
                        margin-bottom: 1rem;
                      }
                    `}
                  >
                    {header.body}
                  </Text>
                )}
              </>
            </ClientOnly>
          )}
        </Box>
      </Grid>
    );
  }
);

export default SoldOutMask;
